<script lang="ts">
  import { _ } from 'svelte-i18n';
  import {
    LayoutDashboard,
    Building2,
    UserRound,
    Radar,
    FileSpreadsheet,
    Bookmark,
    Loader2,
    Plus,
    Users
  } from 'lucide-svelte';

  import { guidedDoor, guidedStep, type Door } from '$lib/stores/guided';
  import { showDoor, showList } from '$lib/stores/navigate';
  import { showFrontDoor } from '$lib/stores/navigate';
  import { exportHistoryOpen } from '$lib/stores/ui';
  import SavedListDialog from '$lib/components/SavedListDialog.svelte';
  import { notifications } from '$lib/stores/notifications';
  import { makeSavedList, openList, savedLists, savedListsLoading } from '$lib/stores/savedlists';
  import type { ListScope } from '$lib/api/savedlists';

  /**
   * The members' sidebar, beside the home screen.
   *
   * # It is the doors, not a second menu
   *
   * Every entry under "Listen erstellen" opens the same door the tile at the
   * foot of the home screen opens — `showDoor` in the navigation module, shared so
   * the two cannot drift. Nothing here is a new way to do anything; it is the
   * existing four ways in, in the place somebody looks for navigation rather
   * than only at the bottom of a screen they have to scroll to.
   *
   * # Why the filter panel is not here
   *
   * The sidebar holds `Filters` everywhere else, and on the home screen there
   * is nothing to filter: no selection has been made. The guide's whole
   * purpose is to produce one, so offering the panel that narrows it beside
   * the screen that creates it is two answers to a question only one of them
   * has been asked.
   */

  const doors: Array<{ id: Door; icon: typeof Building2 }> = [
    { id: 'industry', icon: Building2 },
    { id: 'role', icon: UserRound },
    { id: 'similarity', icon: Radar },
    { id: 'list', icon: FileSpreadsheet }
  ];

  /**
   * Whether the home screen is what is showing.
   *
   * The sidebar is only drawn there today, so this is always true — it marks
   * the entry rather than deciding anything, and stays correct if the nav is
   * ever shown beside a door's steps as well.
   */
  $: onDashboard =
    $guidedStep === 'door' && $guidedDoor === null && $openList === null && !$exportHistoryOpen;

  /** Whether the create dialog is up. */
  let making = false;

  // `showFrontDoor` and `showDoor` say everything that has to be true on
  // arrival, so this component no longer has to remember that going home also
  // means closing an open list. See $lib/stores/navigate.

  async function make(name: string, scope: ListScope) {
    const created = await makeSavedList(name, scope);
    making = false;
    // Straight into the list that was just made. Somebody who names a list is
    // about to put something in it, and leaving them on the screen they were
    // on makes the click look like it did nothing.
    await show(created.id);
  }

  async function show(id: string) {
    try {
      await showList(id);
    } catch {
      notifications.error($_('savedLists.nav.openFailed'));
    }
  }
</script>

<nav class="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-3 py-4">
  <ul>
    <li>
      <button
        type="button"
        on:click={showFrontDoor}
        aria-current={onDashboard ? 'page' : undefined}
        class="flex w-full items-center gap-3 rounded-control px-3 py-2 text-sm font-medium transition-colors {onDashboard
          ? 'bg-surface-panel font-semibold text-accent-700 shadow-raised'
          : 'text-gray-700 hover:bg-surface-panel/60'}"
      >
        <LayoutDashboard class="h-4 w-4 shrink-0" />
        {$_('memberNav.dashboard')}
      </button>
    </li>
  </ul>

  <div>
    <p class="mb-2 px-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
      {$_('memberNav.buildList')}
    </p>
    <ul class="space-y-0.5">
      {#each doors as door (door.id)}
        <li>
          <button
            type="button"
            on:click={() => showDoor(door.id)}
            class="group flex w-full items-center gap-3 rounded-control px-3 py-2 text-left text-sm text-gray-700 transition-colors hover:bg-surface-canvas hover:text-gray-900"
          >
            <svelte:component
              this={door.icon}
              class="h-4 w-4 shrink-0 text-gray-400 transition-colors group-hover:text-accent-600"
            />
            <span class="min-w-0 flex-1 truncate">{$_(`memberNav.doors.${door.id}`)}</span>
          </button>
        </li>
      {/each}
    </ul>
  </div>

  <div>
    <div class="mb-2 flex items-center justify-between gap-2 px-3">
      <p class="text-xs font-semibold uppercase tracking-wide text-gray-500">
        {$_('savedLists.nav.heading')}
      </p>
      <button
        type="button"
        on:click={() => (making = true)}
        title={$_('savedLists.nav.new')}
        class="rounded-control p-1 text-gray-400 transition-colors hover:bg-surface-canvas hover:text-accent-600"
      >
        <span class="sr-only">{$_('savedLists.nav.new')}</span>
        <Plus class="h-4 w-4" />
      </button>
    </div>

    {#if $savedListsLoading && $savedLists.length === 0}
      <p class="flex items-center gap-2 px-3 py-2 text-sm text-gray-400">
        <Loader2 class="h-3.5 w-3.5 animate-spin" />
        {$_('savedLists.nav.loading')}
      </p>
    {:else if $savedLists.length === 0}
      <!--
        One line offering the first one, rather than a heading over nothing.
        The button above does the same thing; this is the one somebody who has
        never made a list will actually see.
      -->
      <button
        type="button"
        on:click={() => (making = true)}
        class="w-full rounded-control px-3 py-2 text-left text-sm text-gray-500 transition-colors hover:bg-surface-canvas hover:text-gray-700"
      >
        {$_('savedLists.nav.empty')}
      </button>
    {:else}
      <ul class="space-y-0.5">
        {#each $savedLists as list (list.id)}
          <li>
            <button
              type="button"
              on:click={() => show(list.id)}
              aria-current={$openList?.id === list.id ? 'page' : undefined}
              class="group flex w-full items-center gap-2 rounded-control px-3 py-2 text-left text-sm transition-colors {$openList?.id ===
              list.id
                ? 'bg-surface-panel font-semibold text-accent-700 shadow-raised'
                : 'text-gray-700 hover:bg-surface-panel/60'}"
            >
              {#if list.scope === 'organisation'}
                <!-- Shared, and whether it is this account's own. The owner is
                     deliberately not named — `mine` exists so the app can tell
                     without publishing who made it. -->
                <Users class="h-4 w-4 shrink-0 {list.mine ? 'text-gray-400' : 'text-accent-500'}" />
              {:else}
                <Bookmark class="h-4 w-4 shrink-0 text-gray-400" />
              {/if}
              <span class="min-w-0 flex-1 truncate">{list.name}</span>
              <span class="shrink-0 text-xs tabular-nums text-gray-400">{list.entries}</span>
            </button>
          </li>
        {/each}
      </ul>
    {/if}
  </div>
</nav>

{#if making}
  <SavedListDialog onSave={make} onCancel={() => (making = false)} />
{/if}
